import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Weekly } from "@/lib/api";
import { fmtDate, fmtMoney } from "@/lib/bill-utils";
import {
  Printer,
  ArrowLeft,
  ArrowUpCircle,
  ArrowDownCircle,
  AlertTriangle,
  CheckCircle2,
  Target,
  Lightbulb,
} from "lucide-react";
import { toast } from "sonner";
import { BillyMark } from "@/components/BillyLogo";
import {
  BarChart,
  Bar as RBar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

// ----- Date helpers -----
const toIso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const startOfWeek = (d) => {
  const dt = new Date(d);
  const day = (dt.getDay() + 6) % 7;
  dt.setDate(dt.getDate() - day);
  dt.setHours(0, 0, 0, 0);
  return dt;
};
const endOfWeek = (d) => {
  const dt = startOfWeek(d);
  dt.setDate(dt.getDate() + 6);
  return dt;
};
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const addMonths = (d, n) => {
  const nd = new Date(d);
  nd.setMonth(nd.getMonth() + n);
  return nd;
};

// ----- Presets -----
const buildPresets = () => {
  const today = new Date();
  const lastWeekRef = new Date(today);
  lastWeekRef.setDate(lastWeekRef.getDate() - 7);
  const lastMonthRef = addMonths(today, -1);
  const next3End = endOfMonth(addMonths(today, 2));
  const last3Start = startOfMonth(addMonths(today, -2));
  return [
    {
      key: "this-week",
      label: "This week",
      from: toIso(startOfWeek(today)),
      to: toIso(endOfWeek(today)),
    },
    {
      key: "last-week",
      label: "Last week",
      from: toIso(startOfWeek(lastWeekRef)),
      to: toIso(endOfWeek(lastWeekRef)),
    },
    {
      key: "this-month",
      label: "This month",
      from: toIso(startOfMonth(today)),
      to: toIso(endOfMonth(today)),
    },
    {
      key: "last-month",
      label: "Last month",
      from: toIso(startOfMonth(lastMonthRef)),
      to: toIso(endOfMonth(lastMonthRef)),
    },
    {
      key: "next-3-months",
      label: "Next 3 months",
      from: toIso(startOfMonth(today)),
      to: toIso(next3End),
    },
    {
      key: "last-3-months",
      label: "Last 3 months",
      from: toIso(last3Start),
      to: toIso(endOfMonth(today)),
    },
  ];
};

// ----- Smart tips (rules-based, no LLM cost) -----
const buildTips = (s, rangeKey) => {
  if (!s) return [];
  const tips = [];
  const span =
    rangeKey && rangeKey.includes("month")
      ? "month"
      : rangeKey && rangeKey.includes("3-month")
        ? "quarter"
        : "period";

  // Net position headline
  if (s.net_position >= 0) {
    tips.push({
      tone: "good",
      title: `Positive cashflow this ${span}.`,
      body: `${fmtMoney(s.incoming_total)} in vs ${fmtMoney(s.outgoing_total)} out leaves a net of ${fmtMoney(s.net_position)}. Bank the surplus or set it aside for tax/GST.`,
    });
  } else {
    tips.push({
      tone: "warn",
      title: `Cashflow gap of ${fmtMoney(Math.abs(s.net_position))} this ${span}.`,
      body: `${fmtMoney(s.outgoing_total)} in bills against just ${fmtMoney(s.incoming_total)} booked income. Chase outstanding deposits, push install dates earlier, or log any quotes you're confident will land.`,
    });
  }

  // Overdue
  if (s.overdue_total > 0) {
    tips.push({
      tone: "bad",
      title: `${fmtMoney(s.overdue_total)} sitting overdue across ${s.overdue_count} bill${s.overdue_count === 1 ? "" : "s"}.`,
      body:
        "These are bills due BEFORE this period started — they're still unpaid. Contact those suppliers/customers and clear the backlog before tackling new bills.",
    });
  } else {
    tips.push({
      tone: "good",
      title: "No overdue bills — all accounts current.",
      body:
        "Solid. Keep this rolling by paying on the day a bill drops, not the day it's due.",
    });
  }

  // Received vs Expected coverage
  if (s.incoming_total > 0) {
    const coverage = s.incoming_received_total / s.incoming_total;
    if (coverage < 0.5 && s.incoming_expected_total > 200) {
      tips.push({
        tone: "warn",
        title: `Most of this ${span}'s income is still unpaid.`,
        body: `Only ${fmtMoney(s.incoming_received_total)} has actually landed — ${fmtMoney(s.incoming_expected_total)} is still pending. Send pay-link SMS reminders from the Bill detail page to speed it up.`,
      });
    } else if (coverage >= 0.8) {
      tips.push({
        tone: "good",
        title: `${Math.round(coverage * 100)}% of expected income already received.`,
        body:
          "Strong collection rhythm. Most clients are paying on time — keep using the public pay-links.",
      });
    }
  } else if (s.outgoing_total > 0) {
    tips.push({
      tone: "warn",
      title: `No income booked for this ${span}.`,
      body:
        "Open the Income tab and log any expected deposits, scheduled installs or repair callouts. Without income forecast, this report under-represents what's coming in.",
    });
  }

  // Volume tip
  if (s.bills.length > 10) {
    tips.push({
      tone: "info",
      title: `${s.bills.length} bills in this ${span} — heavy load.`,
      body:
        "Consider consolidating recurring suppliers (single monthly invoice instead of weekly) so admin time stays low.",
    });
  }

  return tips;
};

// ----- Build chart series (split range into ~4-8 buckets) -----
const buildChartData = (s) => {
  if (!s) return [];
  const from = new Date(s.date_from);
  const to = new Date(s.date_to);
  const days = Math.floor((to - from) / (1000 * 60 * 60 * 24)) + 1;
  // Bucket: weekly if <=14d, fortnightly if <=45d, monthly otherwise
  let bucket;
  if (days <= 14) bucket = { type: "day", size: 1, fmt: (d) => `${d.getDate()}/${d.getMonth() + 1}` };
  else if (days <= 45) bucket = { type: "week", size: 7, fmt: (d) => `${d.getDate()}/${d.getMonth() + 1}` };
  else
    bucket = {
      type: "month",
      size: 30,
      fmt: (d) =>
        d.toLocaleString("en-AU", { month: "short", year: "2-digit" }),
    };

  // Initialise buckets
  const buckets = [];
  let cursor = new Date(from);
  while (cursor <= to) {
    let bEnd;
    if (bucket.type === "month") {
      bEnd = endOfMonth(cursor);
    } else {
      bEnd = new Date(cursor);
      bEnd.setDate(bEnd.getDate() + bucket.size - 1);
    }
    if (bEnd > to) bEnd = new Date(to);
    buckets.push({
      label: bucket.fmt(cursor),
      from: toIso(cursor),
      to: toIso(bEnd),
      Out: 0,
      Received: 0,
      Expected: 0,
    });
    if (bucket.type === "month") {
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    } else {
      cursor = new Date(bEnd);
      cursor.setDate(cursor.getDate() + 1);
    }
  }

  const inRange = (d, b) => d >= b.from && d <= b.to;
  for (const bill of s.bills || []) {
    const d = bill.due_date;
    if (!d) continue;
    const b = buckets.find((x) => inRange(d, x));
    if (b) b.Out += Number(bill.remaining_balance || 0);
  }
  for (const inc of s.incomes || []) {
    const d = inc.expected_date;
    if (!d) continue;
    const b = buckets.find((x) => inRange(d, x));
    if (!b) continue;
    if (inc.received) b.Received += Number(inc.amount || 0);
    else b.Expected += Number(inc.amount || 0);
  }

  return buckets.map((b) => ({
    ...b,
    Out: Math.round(b.Out),
    Received: Math.round(b.Received),
    Expected: Math.round(b.Expected),
  }));
};

// ----- Components -----
const KPI = ({ label, value, accent = "#000", icon: Icon, hint }) => (
  <div className="border-2 border-black p-4">
    <div className="flex items-start justify-between">
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">
        {label}
      </div>
      {Icon && <Icon className="h-4 w-4 text-neutral-400" />}
    </div>
    <div
      className="mt-2 font-display font-black text-2xl tabular-nums leading-tight"
      style={{ color: accent }}
    >
      {value}
    </div>
    {hint && (
      <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-neutral-500">
        {hint}
      </div>
    )}
  </div>
);

const TipCard = ({ tip }) => {
  const tone =
    tip.tone === "good"
      ? "border-green-600 bg-green-50"
      : tip.tone === "bad"
        ? "border-red-500 bg-red-50"
        : tip.tone === "warn"
          ? "border-amber-500 bg-amber-50"
          : "border-neutral-300 bg-neutral-50";
  return (
    <div className={`border-2 ${tone} p-4 break-inside-avoid`}>
      <div className="flex items-start gap-3">
        <Lightbulb className="h-5 w-5 mt-0.5 text-black flex-shrink-0" />
        <div>
          <div className="font-display font-extrabold text-base leading-snug">
            {tip.title}
          </div>
          <div className="text-sm text-neutral-700 mt-1 leading-snug">
            {tip.body}
          </div>
        </div>
      </div>
    </div>
  );
};

const StatementPrint = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const presets = useMemo(buildPresets, []);

  const [activeKey, setActiveKey] = useState(
    params.get("preset") || "this-month",
  );
  const [from, setFrom] = useState(params.get("from") || "");
  const [to, setTo] = useState(params.get("to") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialise dates from preset if missing
  useEffect(() => {
    if (!from || !to) {
      const p = presets.find((x) => x.key === activeKey) || presets[2];
      setFrom(p.from);
      setTo(p.to);
      setActiveKey(p.key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch whenever range changes
  useEffect(() => {
    if (!from || !to) return;
    setLoading(true);
    Weekly.rangeSummary(from, to)
      .then(setData)
      .catch(() => toast.error("Could not load statement data."))
      .finally(() => setLoading(false));
    setParams(
      activeKey === "custom"
        ? { from, to }
        : { preset: activeKey, from, to },
      { replace: true },
    );
  }, [from, to, activeKey, setParams]);

  const pickPreset = (p) => {
    setActiveKey(p.key);
    setFrom(p.from);
    setTo(p.to);
  };

  const tips = useMemo(() => buildTips(data, activeKey), [data, activeKey]);
  const chartData = useMemo(() => buildChartData(data), [data]);

  const printDate = new Date().toLocaleDateString("en-AU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const rangeLabel = useMemo(() => {
    const p = presets.find((x) => x.key === activeKey);
    return p ? p.label : "Custom range";
  }, [activeKey, presets]);

  return (
    <div
      data-testid="statement-print"
      className="bg-white text-black min-h-screen"
    >
      {/* Action bar — hidden on print */}
      <div className="print:hidden border-b-2 border-black bg-white sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <button
            data-testid="back-btn"
            onClick={() => navigate("/weekly")}
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-neutral-500 hover:text-black"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Weekly
          </button>
          <button
            data-testid="print-btn"
            onClick={() => window.print()}
            className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-[11px] px-4 py-2 hover:bg-white hover:text-black transition-colors inline-flex items-center gap-2"
          >
            <Printer className="h-4 w-4" /> Print / Save PDF
          </button>
        </div>

        {/* Range picker */}
        <div className="max-w-5xl mx-auto px-6 pb-4 flex items-end gap-3 flex-wrap">
          <div className="flex gap-2 flex-wrap" data-testid="preset-buttons">
            {presets.map((p) => (
              <button
                key={p.key}
                data-testid={`preset-${p.key}`}
                onClick={() => pickPreset(p)}
                className={`border-2 px-3 py-2 text-[11px] uppercase tracking-widest font-bold transition-colors ${
                  activeKey === p.key
                    ? "border-black bg-black text-white"
                    : "border-neutral-300 text-neutral-700 hover:border-black hover:text-black"
                }`}
              >
                {p.label}
              </button>
            ))}
            <button
              data-testid="preset-custom"
              onClick={() => setActiveKey("custom")}
              className={`border-2 px-3 py-2 text-[11px] uppercase tracking-widest font-bold transition-colors ${
                activeKey === "custom"
                  ? "border-black bg-black text-white"
                  : "border-neutral-300 text-neutral-700 hover:border-black hover:text-black"
              }`}
            >
              Custom
            </button>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">
                From
              </div>
              <input
                data-testid="date-from"
                type="date"
                value={from}
                onChange={(e) => {
                  setFrom(e.target.value);
                  setActiveKey("custom");
                }}
                className="border-2 border-neutral-300 px-2 py-1.5 text-sm font-mono"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">
                To
              </div>
              <input
                data-testid="date-to"
                type="date"
                value={to}
                onChange={(e) => {
                  setTo(e.target.value);
                  setActiveKey("custom");
                }}
                className="border-2 border-neutral-300 px-2 py-1.5 text-sm font-mono"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Print page */}
      <div className="max-w-5xl mx-auto px-6 py-8 print:py-4 print:px-6">
        {loading || !data ? (
          <div className="text-sm text-neutral-500 py-8">
            Loading statement…
          </div>
        ) : (
          <>
            {/* Header */}
            <header className="border-b-2 border-black pb-5 mb-6 flex items-end justify-between gap-6">
              <div className="flex items-center gap-3">
                <BillyMark className="h-10 w-10" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">
                    Cashflow Statement — {rangeLabel}
                  </div>
                  <h1 className="font-display font-black text-3xl tracking-tight leading-tight">
                    Billy. Statement
                  </h1>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">
                  Period
                </div>
                <div className="font-display font-extrabold text-base">
                  {fmtDate(data.date_from)} — {fmtDate(data.date_to)}
                </div>
                <div className="text-xs text-neutral-500 mt-1">
                  Printed {printDate}
                </div>
              </div>
            </header>

            {/* KPI tiles */}
            <section className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
              <KPI
                label="Going Out"
                value={fmtMoney(data.outgoing_total)}
                accent="#FF3B30"
                icon={ArrowUpCircle}
                hint={`${data.bills.length} bill${data.bills.length === 1 ? "" : "s"}`}
              />
              <KPI
                label="Coming In"
                value={fmtMoney(data.incoming_total)}
                accent="#002FA7"
                icon={ArrowDownCircle}
                hint={`${data.incomes.length} entr${data.incomes.length === 1 ? "y" : "ies"}`}
              />
              <KPI
                label="Received"
                value={fmtMoney(data.incoming_received_total)}
                accent="#008A00"
                icon={CheckCircle2}
                hint="In the door"
              />
              <KPI
                label="Net Position"
                value={fmtMoney(data.net_position)}
                accent={data.net_position >= 0 ? "#008A00" : "#FF3B30"}
                icon={Target}
                hint={data.net_position >= 0 ? "Surplus" : "Shortfall"}
              />
              <KPI
                label="Overdue"
                value={fmtMoney(data.overdue_total)}
                accent={data.overdue_total > 0 ? "#FF3B30" : "#008A00"}
                icon={AlertTriangle}
                hint={
                  data.overdue_total > 0
                    ? `${data.overdue_count} to clear`
                    : "All current"
                }
              />
            </section>

            {/* Chart */}
            {chartData.length > 0 && (
              <section className="border-2 border-black p-5 mb-6 break-inside-avoid">
                <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">
                      Ins vs Outs across the period
                    </div>
                    <div className="font-display font-extrabold text-lg leading-tight">
                      {chartData.length} {chartData.length === 1 ? "bucket" : "buckets"} shown
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-600">
                    <span className="inline-flex items-center gap-1">
                      <span
                        className="inline-block h-2.5 w-2.5"
                        style={{ background: "#FF3B30" }}
                      />{" "}
                      Out
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span
                        className="inline-block h-2.5 w-2.5"
                        style={{ background: "#008A00" }}
                      />{" "}
                      Received
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span
                        className="inline-block h-2.5 w-2.5"
                        style={{ background: "#002FA7" }}
                      />{" "}
                      Expected
                    </span>
                  </div>
                </div>
                <div
                  data-testid="statement-chart"
                  style={{ width: "100%", height: 280, minHeight: 280 }}
                >
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart
                      data={chartData}
                      margin={{ top: 10, right: 8, left: -10, bottom: 0 }}
                    >
                      <CartesianGrid
                        strokeDasharray="2 4"
                        stroke="#E5E7EB"
                        vertical={false}
                      />
                      <XAxis
                        dataKey="label"
                        tick={{
                          fontSize: 11,
                          fontFamily: "IBM Plex Sans",
                          letterSpacing: "0.05em",
                          fontWeight: 700,
                        }}
                        stroke="#111"
                      />
                      <YAxis
                        tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }}
                        stroke="#9CA3AF"
                        tickFormatter={(v) =>
                          v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v
                        }
                      />
                      <Tooltip
                        formatter={(v) =>
                          Number(v).toLocaleString("en-AU", {
                            style: "currency",
                            currency: "AUD",
                          })
                        }
                        contentStyle={{
                          borderRadius: 0,
                          border: "2px solid #111",
                          fontSize: 12,
                        }}
                      />
                      <RBar dataKey="Out" fill="#FF3B30" />
                      <RBar dataKey="Received" fill="#008A00" />
                      <RBar dataKey="Expected" fill="#002FA7" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </section>
            )}

            {/* Tips */}
            {tips.length > 0 && (
              <section className="mb-6 break-inside-avoid">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">
                  What to focus on this {rangeLabel.toLowerCase()}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {tips.map((tip, i) => (
                    <TipCard key={i} tip={tip} />
                  ))}
                </div>
              </section>
            )}

            {/* Outs */}
            <section className="border-2 border-black p-5 mb-6 break-inside-avoid">
              <div className="flex items-center gap-2 mb-3">
                <ArrowUpCircle className="h-4 w-4 text-red-600" />
                <h2 className="font-display font-extrabold uppercase tracking-widest text-base">
                  Outs — Bills due in period
                </h2>
                <span className="ml-auto font-display font-extrabold mono tabular-nums">
                  {fmtMoney(data.outgoing_total)}
                </span>
              </div>
              {data.bills.length === 0 ? (
                <div className="text-sm text-neutral-500">
                  No bills due in this period.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 border-b border-neutral-300">
                      <th className="text-left py-2 font-bold">Company</th>
                      <th className="text-left py-2 font-bold">Invoice #</th>
                      <th className="text-left py-2 font-bold">Due</th>
                      <th className="text-right py-2 font-bold">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.bills.map((b) => (
                      <tr key={b.id} className="border-b border-neutral-100">
                        <td className="py-2 font-display font-bold">
                          {b.company}
                        </td>
                        <td className="py-2 text-neutral-600 mono">
                          #{b.invoice_number}
                        </td>
                        <td className="py-2 text-neutral-600">
                          {fmtDate(b.due_date)}
                        </td>
                        <td className="py-2 text-right font-display font-extrabold mono tabular-nums">
                          {fmtMoney(b.remaining_balance)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            {/* Overdue */}
            {data.overdue_bills && data.overdue_bills.length > 0 && (
              <section className="border-2 border-red-500 p-5 mb-6 break-inside-avoid">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <h2 className="font-display font-extrabold uppercase tracking-widest text-base">
                    Overdue bills (carried over)
                  </h2>
                  <span className="ml-auto font-display font-extrabold mono tabular-nums text-red-700">
                    {fmtMoney(data.overdue_total)}
                  </span>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 border-b border-neutral-300">
                      <th className="text-left py-2 font-bold">Company</th>
                      <th className="text-left py-2 font-bold">Invoice #</th>
                      <th className="text-left py-2 font-bold">Was due</th>
                      <th className="text-right py-2 font-bold">Owed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.overdue_bills.map((b) => (
                      <tr key={b.id} className="border-b border-neutral-100">
                        <td className="py-2 font-display font-bold">
                          {b.company}
                        </td>
                        <td className="py-2 text-neutral-600 mono">
                          #{b.invoice_number}
                        </td>
                        <td className="py-2 text-neutral-600">
                          {fmtDate(b.due_date)}
                        </td>
                        <td className="py-2 text-right font-display font-extrabold mono tabular-nums text-red-700">
                          {fmtMoney(b.remaining_balance)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}

            {/* Ins */}
            <section className="border-2 border-black p-5 mb-6 break-inside-avoid">
              <div className="flex items-center gap-2 mb-3">
                <ArrowDownCircle className="h-4 w-4 text-green-700" />
                <h2 className="font-display font-extrabold uppercase tracking-widest text-base">
                  Ins — Income in period
                </h2>
                <span className="ml-auto font-display font-extrabold mono tabular-nums">
                  {fmtMoney(data.incoming_total)}
                </span>
              </div>
              {data.incomes.length === 0 ? (
                <div className="text-sm text-neutral-500">
                  No income scheduled in this period.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 border-b border-neutral-300">
                      <th className="text-left py-2 font-bold">Client</th>
                      <th className="text-left py-2 font-bold">Category</th>
                      <th className="text-left py-2 font-bold">Expected</th>
                      <th className="text-left py-2 font-bold">Status</th>
                      <th className="text-right py-2 font-bold">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.incomes.map((i) => (
                      <tr key={i.id} className="border-b border-neutral-100">
                        <td className="py-2 font-display font-bold">
                          {i.client_name}
                        </td>
                        <td className="py-2 text-neutral-600">{i.category}</td>
                        <td className="py-2 text-neutral-600">
                          {fmtDate(i.expected_date)}
                        </td>
                        <td className="py-2 text-[10px] uppercase tracking-widest font-bold">
                          {i.received ? (
                            <span className="text-green-700">Received</span>
                          ) : (
                            <span className="text-amber-700">Pending</span>
                          )}
                        </td>
                        <td
                          className={`py-2 text-right font-display font-extrabold mono tabular-nums ${i.received ? "text-green-700" : "text-amber-700"}`}
                        >
                          {fmtMoney(i.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            {/* Footer */}
            <footer className="border-t-2 border-black pt-4 text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold flex items-center justify-between">
              <span>Billy. — Cashflow Statement</span>
              <span>{printDate}</span>
            </footer>
          </>
        )}
      </div>

      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 12mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print\\:hidden { display: none !important; }
          .print\\:py-4 { padding-top: 0 !important; padding-bottom: 0 !important; }
          .print\\:px-6 { padding-left: 0 !important; padding-right: 0 !important; }
          .break-inside-avoid { break-inside: avoid; page-break-inside: avoid; }
        }
      `}</style>
    </div>
  );
};

export default StatementPrint;
